import type {
	MapUpdate,
	MapViewport,
	PlaceSelection,
	SearchTransition
} from './intents';
import type { PropertyFilters } from '~/core/entities/property/filters';

import {
	nextFiltersForAreaClear,
	nextFiltersForAreaSelection,
	nextFiltersForClear,
	nextFiltersForFilterChange,
	nextFiltersForLink,
	nextFiltersForLocation,
	nextFiltersForMacroregion,
	nextFiltersForMapMove,
	nextFiltersForPlace,
	nextFiltersForSeeAllCity
} from './intents';

export interface SeeAllCityInput {
	city?: string;
	state?: string;
	viewport?: MapViewport;
}
export interface PropertySearchActions {
	setFilters(next: PropertyFilters): Promise<unknown>;
	setLocation(next: PropertyFilters): Promise<unknown>;
	followLink(next: PropertyFilters, macroregion?: string): Promise<unknown>;
	seeAllCity(input: SeeAllCityInput): Promise<unknown>;
	moveMap(next: PropertyFilters): Promise<unknown>;
	selectAreas(
		base: PropertyFilters,
		h3Ids: string[],
		viewport: MapViewport
	): Promise<unknown>;
	clearAreaSelection(next: PropertyFilters): Promise<unknown>;
	selectPlace(place: PlaceSelection): Promise<unknown>;
	selectAddress(place: PlaceSelection): Promise<unknown>;
	exploreFromSuggest(place?: PlaceSelection): Promise<unknown>;
	setMacroregion(macroregion?: string): Promise<unknown>;
	clear(): Promise<unknown>;
	// The camera decision of the gesture that produced this URL, read once.
	takeMapUpdate(url: string): MapUpdate | undefined;
}
export type IntentActions = Omit<PropertySearchActions, 'takeMapUpdate'>;
export type Navigate = (transition: SearchTransition) => Promise<unknown>;

// Every gesture as a pure rule followed by one navigation.
export function bindIntents(
	current: () => PropertyFilters,
	navigate: Navigate
): IntentActions {
	const place = (selection: PlaceSelection) =>
		navigate(nextFiltersForPlace(current(), selection));
	return {
		setFilters: next => navigate(nextFiltersForFilterChange(current(), next)),
		setLocation: next => navigate(nextFiltersForLocation(next)),
		followLink: (next, macroregion) =>
			navigate(nextFiltersForLink(next, macroregion)),
		seeAllCity: input => navigate(nextFiltersForSeeAllCity(current(), input)),
		moveMap: next => navigate(nextFiltersForMapMove(next)),
		selectAreas: (base, h3Ids, viewport) =>
			navigate(nextFiltersForAreaSelection(base, h3Ids, viewport)),
		clearAreaSelection: next => navigate(nextFiltersForAreaClear(next)),
		selectPlace: place,
		selectAddress: place,
		exploreFromSuggest: selection =>
			selection ? place(selection) : navigate(nextFiltersForClear(current())),
		setMacroregion: value =>
			navigate(nextFiltersForMacroregion(current(), value)),
		clear: () => navigate(nextFiltersForClear(current()))
	};
}

// The adapter encodes "," as %2C while the router keeps it literal; compare decoded.
export function isSameUrl(a: string, b: string): boolean {
	const left = new URL(a, 'http://local');
	const right = new URL(b, 'http://local');
	const query = (url: URL) => [...url.searchParams].sort().join('&');
	return (
		decodedPath(left) === decodedPath(right) && query(left) === query(right)
	);
}

function decodedPath(url: URL): string {
	try {
		return decodeURI(url.pathname);
	} catch {
		// Malformed escapes: compare the raw path rather than failing the map watcher.
		return url.pathname;
	}
}
